import React, {useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Select} from '@mui/material';
import PropTypes from 'prop-types';
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";
import SoftButton from "../../../components/SoftButton";

const AddEditRoomDialog = ({open, onClose, mapping, isEdit, onSubmit, branch}) => {
    const initialFormState = {
        roomId: '',
        cameraId: '',
        configId: '',
        status: '',
    }

    const [formData, setFormData] = useState(initialFormState);
    const [error, setError] = useState(null);
    const [cctv, setCCTV] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [selectedCCTV, setSelectedCCTV] = useState({});

    useEffect(() => {
        setCCTV(branch.cctv)
        setRooms(branch.rooms)
        setCameras(branch?.cctv?.[0]?.cameras)
        if (isEdit && mapping) {
            setFormData({
                roomId: mapping?.room?.roomId || '',
                cameraId: mapping?.camera?.cameraId || '',
                configId: mapping?.cctv?.configId || '',
                status: mapping?.camera?.status || 'ACTIVE',
            })
            const selectedCCTV = branch.cctv.find(c => c.configId === mapping?.cctv?.configId);
            setSelectedCCTV(selectedCCTV);
            if (selectedCCTV) {
                setCameras(selectedCCTV.cameras);
            }
        } else if (open) {
            setFormData(initialFormState);
        }
    }, [isEdit, mapping, open]);

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
        onClose();
    };

    function handleChangeCCTV(event) {
        const selectedCCTV = branch.cctv.find(c => c.configId === event.target.value);
        if (selectedCCTV) {
            setCameras(selectedCCTV.cameras);
            setFormData({
                ...formData,
                configId: selectedCCTV.configId,
                cameraId: selectedCCTV.cameras[0]?.cameraId || '',
            });
        } else {
            setCameras([]);
            setFormData({
                ...formData,
                configId: '',
                cameraId: '',
            });
        }
        setSelectedCCTV(selectedCCTV);
    }

    function handleChangeRoom(event) {
        const selectedRoom = branch.rooms.find(c => c.roomId === event.target.value);
        if (selectedRoom) {
            setFormData({
                ...formData,
                roomId: selectedRoom.roomId || '',
            });
        } else {
            setFormData({
                ...formData,
                roomId: '',
            });
        }
    }

    function handleChangeCamera(event) {
        const selectedCamera = selectedCCTV.cameras.find(c => c.cameraId === event.target.value);
        if (selectedCamera) {
            setFormData({
                ...formData,
                cameraId: selectedCamera?.cameraId || '',
            });
        } else {
            setFormData({
                ...formData,
                cameraId: '',
            });
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Camera Room Mapping' : 'Add Camera Room Mapping'}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    CCTV
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                fullWidth
                                type="select"
                                name="configId"
                                value={formData.configId}
                                onChange={(event) => handleChangeCCTV(event)}
                                label="CCTV"
                                inputProps={{'aria-label': 'CCTV Select'}}
                            >
                                {cctv && cctv.map(cctv => (
                                    <MenuItem key={cctv.configId} value={cctv.configId}>
                                        {cctv.locationDescription}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Camera
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                fullWidth
                                name="cameraId"
                                value={formData.cameraId}
                                onChange={(e) => handleChangeCamera(e)}
                                label="Camera"
                                inputProps={{'aria-label': 'Camera Select'}}
                            >
                                {cameras && cameras.map(cctv => (
                                    <MenuItem key={cctv.cameraId} value={cctv.cameraId}>
                                        {cctv.channel}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Room
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                fullWidth
                                type="select"
                                name="roomId"
                                value={formData.roomId}
                                onChange={(e) => handleChangeRoom(e)}
                                label="Room"
                                inputProps={{'aria-label': 'Room Select'}}
                            >
                                {rooms && rooms.map(room => (
                                    <MenuItem key={room.roomId} value={room.roomId}>
                                        {room.name}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography
                                    component="label"
                                    variant="caption"
                                    fontWeight="bold"
                                >
                                    Status
                                </SoftTypography>
                            </SoftBox>
                            <Select
                                fullWidth
                                type="select"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                label="Status"
                                inputProps={{'aria-label': 'Status Select'}}
                            >
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">InActive</MenuItem>
                            </Select>
                        </Grid>

                    </Grid>
                    {error && (
                        <div style={{color: "red", marginTop: "10px"}}>
                            {error}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <SoftButton type="submit" variant="contained" color="primary" disable={error}>
                        {isEdit ? "Update Mapping" : "Add Mapping"}
                    </SoftButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

AddEditRoomDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    mapping: PropTypes.object,
    branch: PropTypes.object,
    isEdit: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default AddEditRoomDialog;
